/**
 * Whether the browser says there is no connection.
 *
 * **Only one direction of this is knowable, and the API's shape hides that.** `navigator.onLine ===
 * false` means the machine has no route to anywhere and is reliable; `true` means only that a
 * network interface is up, which a captive portal, a dead uplink and a firewalled café all satisfy.
 * So this reports `offline` rather than `online`, and every reader of it is written to say something
 * about a *definite* absence. Anything that needs to know the connection actually works asks the
 * instance, which is what every screen already does — and 4a's `network` copy is what it says when
 * the answer is no.
 *
 * That is the same distinction 4c drew for the media session's `status: null`: there is a difference
 * between stating a fact and declining to state one, and an API that offers a boolean for both is
 * inviting the wrong claim.
 */

export function isOffline(): boolean {
  return globalThis.navigator.onLine === false;
}

/** The two events, as one subscription. Shaped for `useSyncExternalStore`. */
export function subscribeOffline(onChange: () => void): () => void {
  globalThis.addEventListener('online', onChange);
  globalThis.addEventListener('offline', onChange);
  return () => {
    globalThis.removeEventListener('online', onChange);
    globalThis.removeEventListener('offline', onChange);
  };
}
